import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { errorAPI } from "../api";
import {
  Clock,
  Wrench,
  Image as ImageIcon,
  CheckCircle2,
  AlertTriangle,
  Cpu,
  MessageSquare,
  PlusCircle,
} from "lucide-react";
import clsx from "clsx";
import { motion } from "framer-motion";

const ErrorLogList = () => {
  const navigate = useNavigate();
  const { data: errors, isLoading } = useQuery({
    queryKey: ["error-logs"],
    queryFn: () => errorAPI.getAll().then((res) => res.data.data),
  });

  console.log("Fetched Errors:", errors);

  // Define styles for error statuses to match the new UI
  const statusStyles = {
    Open: {
      badge: "bg-red-100 text-red-700",
      dot: "bg-red-500",
    },
    Pending_Maintenance: {
      badge: "bg-amber-100 text-amber-700",
      dot: "bg-amber-500",
    },
    Resolved: {
      badge: "bg-emerald-100 text-emerald-700",
      dot: "bg-emerald-500",
    },
    In_Progress: {
      badge: "bg-blue-100 text-blue-700",
      dot: "bg-blue-500",
    },
  };

  if (isLoading)
    return (
      <div className="p-12 text-center text-slate-500">
        <Cpu className="w-8 h-8 mx-auto mb-3 animate-pulse text-blue-500" />
        Loading bug reports...
      </div>
    );

  return (
    <div className="space-y-6 pb-12">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Machine Error Logs
          </h1>
          <p className="text-slate-500">
            Track and manage reported machine errors and schedule corrective
            maintenance.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/errors/report" className="btn btn-primary shadow-sm gap-2">
            <PlusCircle className="w-4 h-4" /> Report Issue
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
        {errors?.length === 0 ? (
          <div className="p-16 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-800 mb-2">
              All Clear!
            </h3>
            <p className="text-slate-500">
              No active machine error logs reported at this time.
            </p>
          </div>
        ) : (
          <table className="w-full text-left border-collapse">
            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
              <tr>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider w-[20%]">
                  Machine
                </th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider w-[40%]">
                  Issue Description
                </th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider w-[15%]">
                  Status
                </th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider w-[25%] text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {errors?.map((err, index) => (
                <motion.tr
                  key={err._id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="hover:bg-slate-50 transition-colors cursor-pointer"
                >
                  <td className="px-6 py-4">
                    <p className="font-semibold text-slate-900">
                      {err.machineName || "Unknown Machine"}
                    </p>
                    <p className="text-xs text-slate-400 font-mono uppercase">
                      {err.machineId.slice(-8) || "N/A"}
                    </p>
                  </td>
                  <td className="px-6 py-4 max-w-lg">
                    <p className="text-sm text-slate-700 line-clamp-2 leading-relaxed">
                      {err.description}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-xs">
                      <div className="flex items-center gap-1 text-slate-500">
                        <Clock className="w-3 h-3" />
                        <span>
                          Reported:{" "}
                          {new Date(err.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      {err.mediaUrls?.length > 0 && (
                        <div className="flex items-center gap-1 text-blue-500 font-medium">
                          <ImageIcon className="w-3 h-3" />
                          <span>{err.mediaUrls.length} Media File(s)</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {(() => {
                      const statusInfo = statusStyles[err.status] || {
                        badge: "bg-slate-100 text-slate-700",
                        dot: "bg-slate-400",
                      };
                      return (
                        <span
                          className={clsx(
                            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap",
                            statusInfo.badge,
                          )}
                        >
                          <span
                            className={clsx(
                              "h-2 w-2 rounded-full",
                              statusInfo.dot,
                            )}
                          />
                          <span>{err.status.replace(/_/g, " ")}</span>
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {err.status === "Open" ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/maintenance/schedule", {
                            state: {
                              bugId: err._id,
                              machineId: err.machineId,
                              machineName: err.machineName,
                              description: err.description,
                            },
                          });
                        }}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        <Wrench className="w-4 h-4" /> Create Ticket
                      </button>
                    ) : err.status === "Resolved" ? (
                      <span className="text-emerald-600 flex items-center justify-end gap-1 text-sm font-medium">
                        <CheckCircle2 className="w-4 h-4" /> Issue Fixed
                      </span>
                    ) : (
                      <span className="text-amber-600 flex items-center justify-end gap-1 text-sm font-medium">
                        <AlertTriangle className="w-4 h-4" /> Action Required
                      </span>
                    )}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ErrorLogList;
